import { useMemo, useCallback } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatCompactNumber, useCurrency } from "../../../utils/helpers";

const IndicatorHighlights = ({ scenarioData, kpiFactorsData }) => {
  const { t } = useTranslation();
  const currency = useCurrency();
  const discountedRoi = scenarioData?.ROI;
  const discountedIrr = scenarioData?.IRR;
  const discountedPayback =  scenarioData?.PaybackTime;

  const checkIfAcceptable = useCallback((kpiName, value) => {
    const isKpiPresent = kpiFactorsData?.find((item) => item?.name === kpiName);
    if (
      value === null ||
      value === undefined ||
      !isKpiPresent ||
      value === "Infinity"
    )
      return false;
    return !!_.inRange(value, isKpiPresent?.min, isKpiPresent?.max);
  }, [kpiFactorsData]);

  const scenarioHighlights = useMemo(() => [
    {
      label: t("result.highlights.indicator.roi"),
      value: `${discountedRoi?.toLocaleString?.("en-US")} %`,
      isAcceptable: kpiFactorsData?.length
        ? checkIfAcceptable("roi", discountedRoi)
        : discountedRoi <= 0
        ? false
        : true,
    },
    {
      label: t("result.highlights.indicator.payback"),
      value: `${discountedPayback} Years`,
      isAcceptable: kpiFactorsData?.length
        ? checkIfAcceptable("payback", discountedPayback)
        : discountedPayback <= 0 ||
          discountedPayback === "Infinity"
        ? false
        : true,
    },
    {
      label: t("result.highlights.indicator.totalCapital"),
      value: `${currency} ${formatCompactNumber(scenarioData?.Capital) || 0}`,
      hoverValue: `${currency} ${scenarioData?.Capital?.toLocaleString?.("en-US") ?? scenarioData?.Capital ?? 0}`,
      isAcceptable: kpiFactorsData?.length
        ? checkIfAcceptable("capitalExpenditure", scenarioData?.Capital)
        : scenarioData?.Capital <= 0
        ? false
        : true,
    },
    {
      label: t("result.highlights.indicator.irr"),
      value: `${parseFloat(discountedIrr?.toLocaleString?.("en-US")).toFixed(2)} %`,
      isAcceptable: kpiFactorsData?.length
        ? checkIfAcceptable("irr", discountedIrr)
        : discountedIrr <= 0
        ? false
        : true,
    },
  ], [scenarioData, kpiFactorsData, checkIfAcceptable, t, currency]);

  const numberOfAcceptables = _.filter(
    scenarioHighlights,
    (i) => i.isAcceptable
  );
  const numberOfNonAcceptables = _.filter(
    scenarioHighlights,
    (i) => !i.isAcceptable
  );

  return (
    <>
      {numberOfAcceptables?.length > 0 && (
        <p className="mb-0" style={{ fontSize: "18px" }}>
          {numberOfAcceptables?.length} {" "}
          {numberOfAcceptables?.length === 1
            ? t("result.highlights.indicator.singularIs")
            : t("result.highlights.indicator.pluralAre")} {" "}
          <span style={{ color: "#5fc65f", fontWeight: "bold" }}>
            {t("result.highlights.indicator.acceptable")}
          </span>
        </p>
      )}
      <div id="indicator-highlight-1" style={{ display: "none" }}>
        {numberOfAcceptables?.length}
      </div>
      <div id="indicator-highlight-2" style={{ display: "none" }}>
        {numberOfNonAcceptables?.length}
      </div>
      {numberOfNonAcceptables?.length > 0 && (
        <p className="mb-0" style={{ fontSize: "18px" }}>
          {numberOfNonAcceptables?.length} {" "}
          {numberOfNonAcceptables?.length === 1
            ? t("result.highlights.indicator.singularIs")
            : t("result.highlights.indicator.pluralAre")} {" "}
          <span style={{ fontWeight: "bold", color: "#ffa924" }}>
            {t("result.highlights.indicator.notAcceptable")}
          </span>
        </p>
      )}
      <div
        className="mt-2 mb-3 w-100 d-flex align-items-center justify-content-left flex-wrap"
        style={{ gap: "1.5rem" }}
      >
        {scenarioHighlights?.map((item) => (
          <div
            key={item?.label}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "1rem 0 1rem 0",
              minWidth: 150,
              minHeight: 70,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: 0,
              }}
            >
              <p
                className="mb-0"
                style={{
                  fontSize: "12px",
                  margin: 0,
                  lineHeight: 1.6,
                  whiteSpace: "nowrap",
                  wordBreak: "keep-all",
                }}
              >
                {item?.label}
              </p>
              <p
                className="mb-0"
                style={{ margin: 0, lineHeight: 1.6 }}
                title={item?.hoverValue || undefined}
              >
                {item?.value}
              </p>
              <p
                className="mb-0"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: item?.isAcceptable ? "#5fc65f" : "#ffa924",
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                {item?.isAcceptable ? t("result.highlights.indicator.acceptableUpper") : t("result.highlights.indicator.notAcceptableUpper")}
              </p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

IndicatorHighlights.propTypes = {
  data: PropTypes.objectOf({
    netPresentCost: PropTypes.number,
    refNetPresentCost: PropTypes.number,
    lcoeUtility: PropTypes.number,
    refLcoeUtility: PropTypes.number,
    co2Emission: PropTypes.number,
    refCo2Emission: PropTypes.number,
    payback: PropTypes.number,
    roi: PropTypes.number,
  }),
};

export default IndicatorHighlights;
